import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isSameMinute,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { SunIcon, SunriseIcon, SunsetIcon } from 'lucide-react'
import { useContext } from 'react'

import { Button } from '@/components/ui/button'
import { ActiveTenantContext } from '@/context/common/Context'
import { useDashboard } from '@/hooks/useDashboard'

type Buttons =
  | 'today'
  | 'tomorrow'
  | 'week'
  | 'month'
  | 'nextWeek'
  | 'nextMonth'
  | 'morning'
  | 'noon'
  | 'evening'

const CalendarFooter = () => {
  const { selectedFrom, selectedUntil, setSelectedFrom, setSelectedUntil } = useDashboard()
  const { tenant } = useContext(ActiveTenantContext)

  const isButtonActive = (button: Buttons): boolean => {
    if (!selectedFrom || !selectedUntil) return false

    const tenantTimezone = tenant?.timezone || 'Europe/Berlin'
    const now = new Date()
    const today = startOfDay(toZonedTime(now, tenantTimezone))

    switch (button) {
      case 'today':
        return isSameMinute(selectedFrom, today) && isSameMinute(selectedUntil, endOfDay(today))
      case 'tomorrow':
        const tomorrow = startOfDay(addDays(today, 1))
        return (
          isSameMinute(selectedFrom, tomorrow) && isSameMinute(selectedUntil, endOfDay(tomorrow))
        )
      case 'week':
        return (
          isSameMinute(selectedFrom, startOfWeek(today)) &&
          isSameMinute(selectedUntil, endOfWeek(today))
        )
      case 'month':
        return (
          isSameMinute(selectedFrom, startOfMonth(today)) &&
          isSameMinute(selectedUntil, endOfMonth(today))
        )
      case 'nextWeek':
        const nextWeek = addDays(today, 7)
        return (
          isSameMinute(selectedFrom, startOfWeek(nextWeek)) &&
          isSameMinute(selectedUntil, endOfWeek(nextWeek))
        )
      case 'nextMonth':
        const nextMonth = addMonths(today, 1)
        return (
          isSameMinute(selectedFrom, startOfMonth(nextMonth)) &&
          isSameMinute(selectedUntil, endOfMonth(nextMonth))
        )
      case 'morning':
        return (
          isSameMinute(selectedFrom, startOfDay(today)) &&
          isSameMinute(
            selectedUntil,
            new Date(today.getFullYear(), today.getMonth(), today.getDate(), 11, 59, 59, 999)
          )
        )
      case 'noon':
        return (
          isSameMinute(
            selectedFrom,
            new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0, 0)
          ) &&
          isSameMinute(
            selectedUntil,
            new Date(today.getFullYear(), today.getMonth(), today.getDate(), 17, 59, 59, 999)
          )
        )
      case 'evening':
        return (
          isSameMinute(
            selectedFrom,
            new Date(today.getFullYear(), today.getMonth(), today.getDate(), 18, 0, 0, 0)
          ) && isSameMinute(selectedUntil, endOfDay(today))
        )
      default:
        return false
    }
  }

  const changeDateTo = (button: Buttons) => {
    const tenantTimezone = tenant?.timezone || 'Europe/Berlin'
    const now = new Date()
    const today = startOfDay(toZonedTime(now, tenantTimezone))

    switch (button) {
      case 'today':
        setSelectedFrom(today)
        setSelectedUntil(endOfDay(today))
        break
      case 'tomorrow':
        setSelectedFrom(startOfDay(addDays(today, 1)))
        setSelectedUntil(endOfDay(addDays(today, 1)))
        break
      case 'week':
        setSelectedFrom(startOfWeek(today))
        setSelectedUntil(endOfWeek(today))
        break
      case 'month':
        setSelectedFrom(startOfMonth(today))
        setSelectedUntil(endOfMonth(today))
        break
      case 'nextWeek':
        const nextWeek = addDays(today, 7)
        setSelectedFrom(startOfWeek(nextWeek))
        setSelectedUntil(endOfWeek(nextWeek))
        break
      case 'nextMonth':
        const nextMonth = addMonths(today, 1)
        setSelectedFrom(startOfMonth(nextMonth))
        setSelectedUntil(endOfMonth(nextMonth))
        break
      case 'morning':
        setSelectedFrom(startOfDay(today))
        setSelectedUntil(
          new Date(today.getFullYear(), today.getMonth(), today.getDate(), 11, 59, 59, 999)
        )
        break
      case 'noon':
        setSelectedFrom(
          new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0, 0)
        )
        setSelectedUntil(
          new Date(today.getFullYear(), today.getMonth(), today.getDate(), 17, 59, 59, 999)
        )
        break
      case 'evening':
        setSelectedFrom(
          new Date(today.getFullYear(), today.getMonth(), today.getDate(), 18, 0, 0, 0)
        )
        setSelectedUntil(endOfDay(today))
        break
      default:
        break
    }
  }

  return (
    <div className="m-2 grid grid-cols-2 gap-2">
      <Button
        variant={isButtonActive('today') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('today')
        }}
      >
        Heute
      </Button>
      <Button
        variant={isButtonActive('tomorrow') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('tomorrow')
        }}
      >
        Morgen
      </Button>
      <Button
        variant={isButtonActive('week') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('week')
        }}
      >
        Diese Woche
      </Button>
      <Button
        variant={isButtonActive('nextWeek') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('nextWeek')
        }}
      >
        Nächste Woche
      </Button>
      <Button
        variant={isButtonActive('month') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('month')
        }}
      >
        Diesen Monat
      </Button>
      <Button
        variant={isButtonActive('nextMonth') ? 'default' : 'outline'}
        size="sm"
        onClick={() => {
          changeDateTo('nextMonth')
        }}
      >
        Nächster Monat
      </Button>
      <div className="col-span-2 mt-2 flex w-full justify-around">
        <Button
          variant={isButtonActive('morning') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => changeDateTo('morning')}
        >
          <SunriseIcon />
        </Button>
        <Button
          variant={isButtonActive('noon') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => changeDateTo('noon')}
        >
          <SunIcon />
        </Button>
        <Button
          variant={isButtonActive('evening') ? 'default' : 'ghost'}
          size="sm"
          onClick={() => changeDateTo('evening')}
        >
          <SunsetIcon />
        </Button>
      </div>
    </div>
  )
}

export default CalendarFooter
